import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Mail } from "lucide-react";

/**
 * Floating neon-bordered notice on the Products page.
 * Three states:
 *   'card'  — fully expanded card with copy + Email Us CTA
 *   'pill'  — collapsed pill (default) with "Need Further Customization?"
 *   'mini'  — tiny corner icon (user chose to hide the message)
 * User's chosen state persists across page loads via localStorage.
 */
const STORAGE_KEY = "eurosteel-custom-notice-state";

export default function CustomDesignNotice() {
  const [state, setState] = useState("pill"); // 'card' | 'pill' | 'mini'
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // hydrate from localStorage
    const saved = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY);
    if (saved === "mini" || saved === "pill") setState(saved);
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const go = (next) => {
    setState(next);
    // Only persist minimized/pill states (card is always transient)
    if (next !== "card")
      localStorage.setItem(STORAGE_KEY, next);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="custom-design-notice"
      className="pointer-events-none fixed right-4 z-[60] flex justify-end sm:right-10"
      style={{
        bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {state === "card" && (
          <motion.div
            key="card"
            initial={{ opacity: 0, y: 30, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
            style={{ animation: "neon-pulse 3s ease-in-out infinite" }}
            className="pointer-events-auto relative w-[calc(100vw-2rem)] max-w-[300px] rounded-md border border-[#c6a87c] bg-[#0a0a0c] p-6 text-white sm:w-[340px] sm:max-w-none"
          >
            <button
              type="button"
              onClick={() => go("pill")}
              data-testid="custom-design-close"
              className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-white/50 transition-colors hover:text-[#c6a87c]"
              aria-label="Collapse"
            >
              <X size={14} />
            </button>

            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-[#c6a87c]" strokeWidth={1.6} />
              <p className="font-tech text-[10px] uppercase tracking-[0.32em] text-[#c6a87c]">
                Bespoke Design
              </p>
            </div>

            <h3
              className="mt-4 font-serif-display text-xl font-semibold leading-tight text-white sm:text-2xl"
              style={{
                textShadow:
                  "0 0 8px rgba(198,168,124,0.65), 0 0 16px rgba(198,168,124,0.35)",
              }}
            >
              Need further customization?
            </h3>

            <p className="mt-3 font-body text-[13px] leading-[1.65] text-white/75">
              Anything outside the standard configurations — bespoke sizes, custom profiles,
              feature hardware, one-off finishes. Email the studio directly and we'll build
              you a quote.
            </p>

            <a
              href="mailto:orders@euro-steel.co.nz?subject=Custom%20Design%20Enquiry"
              data-testid="custom-design-email-btn"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#c6a87c] py-3 font-tech text-[10px] font-semibold uppercase tracking-[0.28em] text-[#0a0a0c] transition-colors hover:bg-[#d8bd93]"
            >
              <Mail size={13} strokeWidth={1.8} />
              Email the Studio
            </a>

            <p className="mt-3 text-center font-tech text-[9px] uppercase tracking-[0.28em] text-white/40">
              orders@euro-steel.co.nz
            </p>

            <button
              type="button"
              onClick={() => go("mini")}
              data-testid="custom-design-hide"
              className="mt-4 block w-full text-center font-tech text-[9px] uppercase tracking-[0.28em] text-white/30 hover:text-[#c6a87c]"
            >
              Hide notice
            </button>
          </motion.div>
        )}

        {state === "pill" && (
          <motion.div
            key="pill"
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.94 }}
            transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
            className="pointer-events-auto flex items-center gap-2"
          >
            <button
              type="button"
              onClick={() => go("card")}
              data-testid="custom-design-toggle"
              style={{
                textShadow:
                  "0 0 6px rgba(198,168,124,0.85), 0 0 12px rgba(198,168,124,0.5)",
                animation: "neon-pulse 2.6s ease-in-out infinite",
              }}
              className="flex items-center gap-2 rounded-full border border-[#c6a87c] bg-[#0a0a0c] px-4 py-2.5 font-tech text-[9px] font-bold uppercase tracking-[0.22em] text-[#c6a87c] transition-colors hover:bg-[#c6a87c]/10 sm:px-5 sm:py-3 sm:text-[10px] sm:tracking-[0.28em]"
            >
              <Sparkles size={13} strokeWidth={1.8} />
              Need Further Customization?
            </button>
            <button
              type="button"
              onClick={() => go("mini")}
              data-testid="custom-design-minimize"
              aria-label="Minimize notice"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-[#c6a87c]/60 bg-[#0a0a0c] text-[#c6a87c]/70 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c]"
            >
              <X size={12} strokeWidth={2} />
            </button>
          </motion.div>
        )}

        {state === "mini" && (
          <motion.button
            key="mini"
            type="button"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
            onClick={() => go("pill")}
            data-testid="custom-design-reopen"
            aria-label="Show customization notice"
            title="Custom design enquiries"
            style={{ animation: "neon-pulse-static 3.4s ease-in-out infinite" }}
            className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border border-[#c6a87c] bg-[#0a0a0c] text-[#c6a87c] transition-colors hover:bg-[#c6a87c]/15"
          >
            <Sparkles size={15} strokeWidth={1.8} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
